import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation } from "@dnd-kit/react/sortable";

import Topbar from "../../../components/topbar/TopBar";
import { SortablePricingVehicleCard } from "../components/SortablePricingVehicleCard";
import { reorderPricingVehicles } from "../domain/reorderPricingVehicles";
import { updatePricingVehicleOrderUseCase } from "../domain/usecases/updatePricingVehicleOrderUseCase";
import { deletePricingVehicleUseCase } from "../domain/usecases/deletePricingVehicleUseCase";
import { getPricingVehicleListUseCase } from "../domain/usecases/getPricingVehicleListUseCase";
import { getProvinceListUseCase } from "../../geography_catalog/province/domain/usecases/getProvinceListUseCase";
import { createVehicleHasProvinceUseCase } from "../domain/usecases/createVehicleHasProvinceUseCase";
import { getVehicleHasProvinceByVehicleIdUseCase } from "../domain/usecases/getVehicleHasProvinceByVehicleIdUseCase";
import { updateVehicleHasProvinceUseCase } from "../domain/usecases/updateVehicleHasProvinceUseCase";

const SERVICE_BOOKING_TABS = [
  { value: 1, label: "AHORA" },
  { value: 2, label: "DESPUES" },
];

function getServiceBookingLabel(value) {
  return Number(value) === 1 ? "Ahora" : "Despues";
}

function getVehicleListByServiceBooking(vehicles, serviceBooking) {
  return vehicles.filter(
    (vehicle) => Number(vehicle.serviceBooking) === Number(serviceBooking),
  );
}

function getInitialAddForm(provinceOptions = []) {
  return {
    provinceId: provinceOptions[0] ? String(provinceOptions[0].id) : "",
    active: true,
    fareBase: "",
  };
}

export const PricingVehicleListPage = () => {
  const navigate = useNavigate();
  const [selectedServiceBooking, setSelectedServiceBooking] = useState(1);
  const [pricingVehicles, setPricingVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addDialogTarget, setAddDialogTarget] = useState(null);
  const [viewDialogTarget, setViewDialogTarget] = useState(null);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [vehicleProvinceList, setVehicleProvinceList] = useState([]);
  const [vehicleProvinceOriginalList, setVehicleProvinceOriginalList] =
    useState([]);
  const [loadingVehicleProvinceList, setLoadingVehicleProvinceList] =
    useState(false);
  const [updatingVehicleProvinceId, setUpdatingVehicleProvinceId] =
    useState(null);
  const [addForm, setAddForm] = useState(() => getInitialAddForm());
  const [savingAdd, setSavingAdd] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const savingOrderRef = useRef(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadPricingVehicles = async () => {
    try {
      setLoading(true);
      setError("");
      setPricingVehicles(await getPricingVehicleListUseCase());
    } catch (requestError) {
      setError(
        requestError.message || "No se pudo cargar la lista de vehiculos.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricingVehicles();
  }, []);

  useEffect(() => {
    const loadProvinces = async () => {
      try {
        setProvinceOptions(await getProvinceListUseCase());
      } catch (requestError) {
        setMessage({
          open: true,
          type: "error",
          text:
            requestError.message || "No se pudo cargar la lista de provincias.",
        });
      }
    };

    loadProvinces();
  }, []);

  useEffect(() => {
    if (!addDialogTarget?.id) {
      return;
    }

    setAddForm(getInitialAddForm(provinceOptions));
  }, [addDialogTarget?.id, provinceOptions]);

  useEffect(() => {
    const loadVehicleProvinceList = async () => {
      if (!viewDialogTarget?.id) {
        setVehicleProvinceList([]);
        setVehicleProvinceOriginalList([]);
        return;
      }

      try {
        setLoadingVehicleProvinceList(true);
        const records = await getVehicleHasProvinceByVehicleIdUseCase(
          viewDialogTarget.id,
        );
        setVehicleProvinceList(records);
        setVehicleProvinceOriginalList(
          records.map((record) => ({
            ...record,
          })),
        );
      } catch (requestError) {
        setMessage({
          open: true,
          type: "error",
          text:
            requestError.message ||
            "No se pudo cargar la lista de provincias del vehiculo.",
        });
      } finally {
        setLoadingVehicleProvinceList(false);
      }
    };

    loadVehicleProvinceList();
  }, [viewDialogTarget?.id]);

  const selectedVehicles = useMemo(
    () =>
      getVehicleListByServiceBooking(pricingVehicles, selectedServiceBooking),
    [pricingVehicles, selectedServiceBooking],
  );

  const handleVehicleDragEnd = async (event) => {
    const operation = event.operation;
    if (savingOrderRef.current || deleting || loading) return;
    if (event.canceled || !operation || operation.canceled) return;
    if (!isSortableOperation(operation)) return;

    const { initialIndex, index } = operation.source;
    const previousVehicles = pricingVehicles;
    const nextVehicles = reorderPricingVehicles(
      previousVehicles,
      selectedServiceBooking,
      initialIndex,
      index,
    );
    if (nextVehicles === previousVehicles) return;

    savingOrderRef.current = true;
    setSavingOrder(true);
    setPricingVehicles(nextVehicles);
    try {
      await updatePricingVehicleOrderUseCase(
        getVehicleListByServiceBooking(nextVehicles, selectedServiceBooking),
      );
      setMessage({ open: true, type: "success", text: "Orden actualizado." });
    } catch (requestError) {
      setPricingVehicles(previousVehicles);
      setMessage({
        open: true,
        type: "error",
        text: "No se pudo guardar el orden de los vehiculos. Intenta nuevamente.",
      });
    } finally {
      savingOrderRef.current = false;
      setSavingOrder(false);
    }
  };

  const handleOpenDeleteDialog = (vehicle) => {
    setDeleteTarget(vehicle);
  };

  const handleOpenInfoDialog = (vehicle, mode) => {
    if (mode === "add") {
      setAddDialogTarget(vehicle);
      setAddForm(getInitialAddForm(provinceOptions));
      return;
    }

    setViewDialogTarget(vehicle);
  };

  const handleCloseAddDialog = () => {
    if (savingAdd) {
      return;
    }

    setAddDialogTarget(null);
    setAddForm(getInitialAddForm(provinceOptions));
  };

  const handleCloseViewDialog = () => {
    setViewDialogTarget(null);
    setVehicleProvinceList([]);
    setVehicleProvinceOriginalList([]);
    setUpdatingVehicleProvinceId(null);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) {
      return;
    }

    setDeleteTarget(null);
  };

  const handleAddFormChange = (event) => {
    const { name, value } = event.target;

    setAddForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddActiveChange = (event) => {
    setAddForm((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleVehicleProvinceChange = (vehicleProvinceId, field, value) => {
    setVehicleProvinceList((prev) =>
      prev.map((item) =>
        String(item.id) === String(vehicleProvinceId)
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );
  };

  const isSameVehicleProvince = (currentItem, originalItem) => {
    if (!originalItem) {
      return false;
    }

    return (
      String(currentItem.id) === String(originalItem.id) &&
      String(currentItem.provinceId) === String(originalItem.provinceId) &&
      String(currentItem.vehicleId) === String(originalItem.vehicleId) &&
      Boolean(currentItem.active) === Boolean(originalItem.active) &&
      Number(currentItem.fareBase) === Number(originalItem.fareBase)
    );
  };

  const handleSaveVehicleProvince = async () => {
    const payload = {
      provinceId: Number(addForm.provinceId),
      vehicleId: addDialogTarget?.id,
      active: Boolean(addForm.active),
      fareBase: Number(addForm.fareBase),
    };

    try {
      setSavingAdd(true);
      const response = await createVehicleHasProvinceUseCase(payload);
      setMessage({
        open: true,
        type: "success",
        text: response?.message || "Provincia asociada correctamente.",
      });
      setAddForm((prev) => ({
        ...prev,
        fareBase: "",
        active: true,
      }));
    } catch (requestError) {
      setMessage({
        open: true,
        type: "error",
        text:
          requestError.message ||
          "No se pudo guardar la provincia del vehiculo.",
      });
    } finally {
      setSavingAdd(false);
    }
  };

  const handleDeletePricingVehicle = async () => {
    if (!deleteTarget?.id) {
      return;
    }

    try {
      setDeleting(true);
      await deletePricingVehicleUseCase(deleteTarget.id);
      setPricingVehicles((prev) =>
        prev.filter((vehicle) => vehicle.id !== deleteTarget.id),
      );
      setMessage({
        open: true,
        type: "success",
        text: "Vehiculo eliminado correctamente.",
      });
      setDeleteTarget(null);
    } catch (requestError) {
      setMessage({
        open: true,
        type: "error",
        text: requestError.message || "No se pudo eliminar el vehiculo.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateVehicleProvince = async (item) => {
    const originalItem = vehicleProvinceOriginalList.find(
      (record) => String(record.id) === String(item.id),
    );

    if (isSameVehicleProvince(item, originalItem)) {
      setMessage({
        open: true,
        type: "success",
        text: "Configuracion actualizada correctamente.",
      });
      return;
    }

    const payload = {
      id: item.id,
      provinceId: Number(item.provinceId),
      vehicleId: Number(item.vehicleId),
      active: Boolean(item.active),
      fareBase: Number(item.fareBase),
    };

    try {
      setUpdatingVehicleProvinceId(item.id);
      const response = await updateVehicleHasProvinceUseCase(payload);
      setMessage({
        open: true,
        type: "success",
        text: response?.message || "Configuracion actualizada correctamente.",
      });
      setVehicleProvinceOriginalList((prev) =>
        prev.map((record) =>
          String(record.id) === String(item.id)
            ? {
                ...record,
                provinceId: Number(item.provinceId),
                vehicleId: Number(item.vehicleId),
                active: Boolean(item.active),
                fareBase: Number(item.fareBase),
              }
            : record,
        ),
      );
    } catch (requestError) {
      setMessage({
        open: true,
        type: "error",
        text:
          requestError.message ||
          "No se pudo actualizar la configuracion del vehiculo.",
      });
    } finally {
      setUpdatingVehicleProvinceId(null);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": {
              color: "#000",
            },
            p: 1,
          }}
        >
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      VEHICULOS
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/admin/precio-vehiculo")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "& .MuiButton-startIcon": {
                        color: "#fff",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#fff",
                      },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Agregar Vehiculo
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, bgcolor: "#fff" }}>
                <Tabs
                  value={selectedServiceBooking}
                  onChange={(event, value) => setSelectedServiceBooking(value)}
                  centered
                  sx={{
                    "& .MuiTab-root": {
                      minHeight: 54,
                      textTransform: "none",
                      fontSize: "0.98rem",
                      fontWeight: 700,
                      color: "#000",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "#000",
                      fontWeight: 800,
                      fontSize: "1.105rem",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#000",
                    },
                  }}
                >
                  {SERVICE_BOOKING_TABS.map((item) => (
                    <Tab
                      key={item.value}
                      value={item.value}
                      label={item.label}
                      disabled={savingOrder}
                    />
                  ))}
                </Tabs>
              </Box>
            </Paper>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(16, 24, 40, 0.08)",
                overflow: "hidden",
                bgcolor: "#fff",
                padding: 2,
              }}
            >
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando vehiculos...
                    </Typography>
                  </Stack>
                ) : null}

                {!loading && selectedVehicles.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 4,
                      textAlign: "center",
                      bgcolor: "#fbfcfc",
                    }}
                  >
                    <Typography fontWeight={700}>
                      Sin vehiculos registrados
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      No hay configuraciones disponibles para este tipo de
                      servicio.
                    </Typography>
                  </Paper>
                ) : null}

                {!loading && selectedVehicles.length > 0 ? (
                  <DragDropProvider onDragEnd={handleVehicleDragEnd}>
                    <Grid container spacing={2} sx={{ rowGap: 3 }}>
                      {selectedVehicles.map((vehicle, index) => (
                        <SortablePricingVehicleCard
                          key={vehicle.id}
                          vehicle={vehicle}
                          index={index}
                          disabled={savingOrder || deleting}
                        >
                          <Stack spacing={1.25}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1.5}
                            >
                              <Box>
                                <Typography variant="h6" fontWeight={800}>
                                  {vehicle.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.5, color: "#000" }}
                                >
                                  {vehicle.active ? "Activo" : "Inactivo"}
                                </Typography>
                              </Box>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                              >
                                <Button
                                  variant="contained"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    handleOpenInfoDialog(vehicle, "add")
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    bgcolor: "#165c53",
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: "#12483f",
                                    },
                                  }}
                                />
                                <Button
                                  startIcon={<VisibilityIcon />}
                                  variant="contained"
                                  onClick={() =>
                                    handleOpenInfoDialog(vehicle, "view")
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    bgcolor: "#165c53",
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: "#12483f",
                                    },
                                  }}
                                />
                              </Stack>
                            </Stack>

                            <Divider />

                            <Typography variant="body2" sx={{ color: "#000" }}>
                              {vehicle.description || "Sin descripcion"}
                            </Typography>
                            {vehicle.info ? (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#4b5563", fontWeight: 700 }}
                                >
                                  Informacion adicional
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#000",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {vehicle.info}
                                </Typography>
                              </Box>
                            ) : null}

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <GroupsIcon sx={{ color: "#165c53" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                Nacional:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {vehicle.maxPersonNational}
                                </Box>
                                {" | "}Internacional:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {vehicle.maxPersonInternational}
                                </Box>
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <LocalTaxiIcon sx={{ color: "#165c53" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                Servicio:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {getServiceBookingLabel(
                                    vehicle.serviceBooking,
                                  )}
                                </Box>
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <TrendingUpIcon sx={{ color: "#c56a19" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                Precio base:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {vehicle.priceBase}
                                </Box>
                                {" | "}Multiplicador:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {vehicle.multiplier}
                                </Box>
                              </Typography>
                            </Stack>

                            <Box sx={{ pt: 0.75 }}>
                              <Stack direction="row" spacing={1.25}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  color="error"
                                  disabled={savingOrder}
                                  onClick={() =>
                                    handleOpenDeleteDialog(vehicle)
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                  }}
                                >
                                  Eliminar
                                </Button>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  onClick={() =>
                                    navigate(
                                      `/admin/precio-vehiculo/update/${vehicle.id}`,
                                    )
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    bgcolor: "#165c53",
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: "#12483f",
                                    },
                                  }}
                                >
                                  Actualizar
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>
                        </SortablePricingVehicleCard>
                      ))}
                    </Grid>
                  </DragDropProvider>
                ) : null}
              </Box>
            </Paper>
          </Stack>

          <Dialog
            open={Boolean(deleteTarget)}
            onClose={handleCloseDeleteDialog}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ color: "#000", fontWeight: 800 }}>
              Confirmar eliminacion
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ color: "#000" }}>
                Desea Eliminar esta Fecha Festiva?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseDeleteDialog}
                disabled={deleting}
                sx={{
                  textTransform: "none",
                  color: "#000",
                  fontWeight: 700,
                }}
              >
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeletePricingVehicle}
                disabled={deleting}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                {deleting ? "Eliminando..." : "Si, eliminar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(addDialogTarget)}
            onClose={handleCloseAddDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                bgcolor: "#fff",
                color: "#000",
                borderRadius: 3,
                overflow: "hidden",
                width: "min(720px, 96vw)",
              },
            }}
          >
            <DialogTitle
              sx={{
                color: "#000",
                fontWeight: 800,
                pr: 6,
                position: "relative",
              }}
            >
              Agregar tarifa
              <IconButton
                aria-label="cerrar"
                onClick={handleCloseAddDialog}
                disabled={savingAdd}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  width: 34,
                  height: 34,
                  border: "1px solid rgba(0,0,0,0.18)",
                  color: "#000",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ bgcolor: "#fff" }}>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  select
                  label="Provincia"
                  name="provinceId"
                  value={addForm.provinceId}
                  onChange={handleAddFormChange}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#000",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#000",
                    },
                  }}
                >
                  {provinceOptions.map((province) => (
                    <MenuItem
                      key={province.id}
                      value={String(province.id)}
                      sx={{ color: "#000" }}
                    >
                      {province.name} - {province.code}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(addForm.active)}
                      onChange={handleAddActiveChange}
                      sx={{
                        color: "#165c53",
                        "&.Mui-checked": {
                          color: "#165c53",
                        },
                      }}
                    />
                  }
                  label="Activo"
                  sx={{
                    mx: 0,
                    "& .MuiFormControlLabel-label": {
                      color: "#000",
                      fontWeight: 600,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Tarifa base"
                  name="fareBase"
                  value={addForm.fareBase}
                  onChange={handleAddFormChange}
                  inputProps={{ min: "1", step: "0.01" }}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#000",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#000",
                    },
                  }}
                />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveVehicleProvince}
                disabled={savingAdd}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "#165c53",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#12483f",
                  },
                }}
              >
                {savingAdd ? "Guardando..." : "Guardar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(viewDialogTarget)}
            onClose={handleCloseViewDialog}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                bgcolor: "#fff",
                color: "#000",
                borderRadius: 3,
                overflow: "hidden",
                maxHeight: "90vh",
                height: "90vh",
                width: "min(1100px, 96vw)",
              },
            }}
          >
            <DialogTitle
              sx={{
                color: "#000",
                fontWeight: 800,
                pr: 6,
                position: "relative",
              }}
            >
              LISTA DE VEHICULO - PROVINCIAS
              <IconButton
                aria-label="cerrar"
                onClick={handleCloseViewDialog}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  width: 34,
                  height: 34,
                  border: "1px solid rgba(0,0,0,0.18)",
                  color: "#000",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                bgcolor: "#fff",
                maxHeight: "calc(90vh - 132px)",
                overflow: "auto",
              }}
            >
              {viewDialogTarget ? (
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Box>
                    <Typography sx={{ color: "#000", fontWeight: 800 }}>
                      {viewDialogTarget.name}
                    </Typography>
                  </Box>

                  {loadingVehicleProvinceList ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                      <CircularProgress size={26} />
                      <Typography color="text.secondary">
                        Cargando provincias del vehiculo...
                      </Typography>
                    </Stack>
                  ) : (
                    <Grid container spacing={2}>
                      {vehicleProvinceList.length > 0 ? (
                        vehicleProvinceList.map((item) => (
                          <Grid item xs={12} md={4} key={item.id}>
                            <Paper
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                p: 2,
                                height: "100%",
                                bgcolor: "#fbfcfc",
                                border: "1px solid rgba(16, 24, 40, 0.14)",
                                boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
                              }}
                            >
                              <Stack spacing={2}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#4b5563", fontWeight: 700 }}
                                  >
                                    Provincia
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#000",
                                      fontWeight: 700,
                                      mt: 0.5,
                                    }}
                                  >
                                    {item.provinceName || "Sin provincia"}
                                  </Typography>
                                </Box>

                                <TextField
                                  select
                                  fullWidth
                                  label="Cambiar provincia"
                                  value={String(item.provinceId || "")}
                                  onChange={(event) =>
                                    handleVehicleProvinceChange(
                                      item.id,
                                      "provinceId",
                                      event.target.value,
                                    )
                                  }
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000",
                                    },
                                    "& .MuiInputLabel-root": {
                                      color: "#000",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#000",
                                    },
                                  }}
                                >
                                  {provinceOptions.map((province) => (
                                    <MenuItem
                                      key={province.id}
                                      value={String(province.id)}
                                      sx={{ color: "#000" }}
                                    >
                                      {province.name} - {province.code}
                                    </MenuItem>
                                  ))}
                                </TextField>

                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={Boolean(item.active)}
                                      onChange={(event) =>
                                        handleVehicleProvinceChange(
                                          item.id,
                                          "active",
                                          event.target.checked,
                                        )
                                      }
                                      sx={{
                                        color: "#165c53",
                                        "&.Mui-checked": {
                                          color: "#165c53",
                                        },
                                      }}
                                    />
                                  }
                                  label="Activo"
                                  sx={{
                                    mx: 0,
                                    "& .MuiFormControlLabel-label": {
                                      color: "#000",
                                      fontWeight: 600,
                                    },
                                  }}
                                />

                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Tarifa base"
                                  value={item.fareBase ?? ""}
                                  onChange={(event) =>
                                    handleVehicleProvinceChange(
                                      item.id,
                                      "fareBase",
                                      event.target.value,
                                    )
                                  }
                                  inputProps={{ min: "1", step: "0.01" }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000",
                                    },
                                    "& .MuiInputLabel-root": {
                                      color: "#000",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#000",
                                    },
                                  }}
                                />

                                <Button
                                  fullWidth
                                  variant="contained"
                                  onClick={() =>
                                    handleUpdateVehicleProvince(item)
                                  }
                                  disabled={
                                    updatingVehicleProvinceId === item.id
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    bgcolor: "#165c53",
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: "#12483f",
                                    },
                                  }}
                                >
                                  {updatingVehicleProvinceId === item.id
                                    ? "Actualizando..."
                                    : "Actualizar"}
                                </Button>
                              </Stack>
                            </Paper>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              p: 3,
                              bgcolor: "#fbfcfc",
                              border: "1px solid rgba(16, 24, 40, 0.14)",
                              boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
                            }}
                          >
                            <Typography sx={{ color: "#000", fontWeight: 700 }}>
                              Sin provincias asociadas
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Stack>
              ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseViewDialog}
                sx={{
                  textTransform: "none",
                  color: "#000",
                  fontWeight: 700,
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default PricingVehicleListPage;
